import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { OperadorasSelectors, UserProfileSelectors } from '@gestor-web/@core/store/selectors';
import { Store } from '@ngrx/store';
import { CONTACT_URL } from '@gestor-web/app/shared/config/feature-access.config';
import { Subject, takeUntil } from 'rxjs';

/**
 * Componente de tooltip para itens bloqueados por falta de módulo.
 *
 * Exibe uma mensagem informando quais módulos são necessários para acessar
 * a funcionalidade e um botão para entrar em contato.
 *
 * Uso:
 * ```html
 * <bl-locked-tooltip [allowedModules]="['Simulador', 'Qualifica', 'Premium']"></bl-locked-tooltip>
 * ```
 */
@Component({
    selector: 'bl-locked-tooltip',
    templateUrl: './locked-tooltip.component.html',
    styleUrls: ['./locked-tooltip.component.scss'],
    standalone: false
})
export class LockedTooltipComponent implements OnInit, OnDestroy {
  @Input() allowedModules: string[] = [];

  @Input() contactUrl: string = CONTACT_URL;

  private destroy$ = new Subject<void>();

  private operadoraNome?: string;
  private usuarioNome?: string;
  private usuarioEmail?: string;
  private usuarioTelefone?: string;
  private usuarioDesafio: string = 'Outro(s)';
  private usuarioMensagem: string =
    'Olá! Possuo interesse em outro(s) módulo(s) do Gestor IDSS. Aguardo retorno para saber mais.';

  constructor(private store: Store) {}

  ngOnInit(): void {
    this.store
      .select(OperadorasSelectors.selectWorkspaceOperadora)
      .pipe(takeUntil(this.destroy$))
      .subscribe(operadora => {
        if (operadora?.sg_operadora) {
          this.operadoraNome = operadora.sg_operadora;
        }
      });

    this.store
      .select(UserProfileSelectors.selectUserIdssProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        if (!user) {
          return;
        }

        this.usuarioNome = user.nm_usuario;
        this.usuarioEmail = user.ds_email;
        this.usuarioTelefone = user.nr_fone_celular;
      });
  }

  get modulesText(): string {
    if (!this.allowedModules || this.allowedModules.length === 0) {
      return '';
    }
    return this.allowedModules.join(', ');
  }

  get isPlural(): boolean {
    return this.allowedModules && this.allowedModules.length > 1;
  }

  openContact(): void {
    const url = new URL(this.contactUrl);

    if (this.operadoraNome?.trim()) {
      url.searchParams.set('operadora', this.operadoraNome.trim());
    }
    if (this.usuarioNome?.trim()) {
      url.searchParams.set('nome', this.usuarioNome.trim());
    }
    if (this.usuarioEmail?.trim()) {
      url.searchParams.set('email', this.usuarioEmail.trim());
    }
    if (this.usuarioTelefone?.trim()) {
      url.searchParams.set('telefone', this.usuarioTelefone.trim());
    }
    if (this.usuarioDesafio?.trim()) {
      url.searchParams.set('desafio', this.usuarioDesafio.trim());
    }

    const mensagem =
      this.allowedModules.length > 0
        ? `Olá! Quero saber mais sobre o(s) módulo(s): ${this.modulesText}.`
        : this.usuarioMensagem;

    if (mensagem?.trim()) {
      url.searchParams.set('mensagem', mensagem.trim());
    }

    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
